// Lightweight client-side shape checks. Server is the source of truth for
// validation; these only exist so the user gets fast feedback in forms.

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;
const MASTER_USERNAME_RE = /^[a-z][a-z0-9._-]{2,29}$/;
const SUB_USERNAME_RE = /^[a-z][a-z0-9._-]{2,29}$/;
const MOBILE_RE = /^[6-9][0-9]{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isGstin(v) {
  return typeof v === 'string' && GSTIN_RE.test(v.toUpperCase());
}

export function isMasterUsername(v) {
  return typeof v === 'string' && MASTER_USERNAME_RE.test(v);
}

export function isSubUsername(v) {
  return typeof v === 'string' && SUB_USERNAME_RE.test(v);
}

export function isMobileIN(v) {
  return typeof v === 'string' && MOBILE_RE.test(v);
}

export function isEmail(v) {
  return typeof v === 'string' && EMAIL_RE.test(v);
}

// ─────────────────────────────────────────────────────────────────
// Input-filter helpers for AntD Form.Item's `getValueFromEvent` prop.
// These strip invalid characters BEFORE they reach form state so the
// user can't even type a non-digit into a mobile/OTP field. Pair with
// `inputMode="numeric"` on the <Input> to get the right mobile keyboard.
//
// Usage:
//   <Form.Item name="contactMobile" getValueFromEvent={digitsOnly(10)} ...>
//     <Input maxLength={10} inputMode="numeric" />
//   </Form.Item>
// ─────────────────────────────────────────────────────────────────

function readEventValue(e) {
  // Some Form.Item flows pass the raw value; AntD <Input> passes a
  // SyntheticEvent. Cover both.
  if (e && e.target && typeof e.target.value === 'string') return e.target.value;
  if (typeof e === 'string') return e;
  return '';
}

export function digitsOnly(maxLen) {
  return (e) => readEventValue(e).replace(/\D/g, '').slice(0, maxLen);
}

export function gstinInput(e) {
  return readEventValue(e)
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 15);
}

// IP-address input mask. Allows the union of characters needed for
// IPv4 (digits, dots), IPv6 (hex digits, colons, dots for v4-mapped
// addresses) AND an optional /N CIDR mask. Caps at 49 chars — the
// longest possible IPv6 string plus '/128'. Final shape correctness
// is still enforced by `looksLikeIp()` and the backend's Joi.ip(...,
// cidr:'optional') check; this just stops obvious junk like letters
// g-z from ever appearing in the field.
export function ipAddressInput(e) {
  return readEventValue(e)
    .replace(/[^0-9a-fA-F.:/]/g, '')
    .slice(0, 49);
}

// Strong password: min 12 chars, mixed case, digit, symbol. Mirrors
// backend assertStrong shape but kept loose — the server is authoritative.
export function passwordStrength(v) {
  if (typeof v !== 'string') return { ok: false, reasons: ['required'] };
  const reasons = [];
  if (v.length < 12) reasons.push('at least 12 characters');
  if (!/[a-z]/.test(v)) reasons.push('a lowercase letter');
  if (!/[A-Z]/.test(v)) reasons.push('an uppercase letter');
  if (!/[0-9]/.test(v)) reasons.push('a digit');
  if (!/[^A-Za-z0-9]/.test(v)) reasons.push('a symbol');
  return { ok: reasons.length === 0, reasons };
}
